import { Route } from 'react-router-dom';
// Only for `bindRoutes`; the page is the `/alpha` plugin's (PAT-1022).
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
// Only for `bindRoutes`; the pages are the `/alpha` plugin's (PAT-1027). The
// route refs are the same objects, so the bindings below still apply.
import { catalogPlugin } from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
// Only for `bindRoutes`; the page is the `/alpha` plugin's (PAT-1025).
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  TeamCreatePage,
  TeamEditPage,
  TeamsPage,
} from '@internal/backstage-plugin-team-management-frontend';
import { orgPlugin } from '@backstage/plugin-org';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { entityPage } from './components/catalog/EntityPage';

import { createApp } from '@backstage/frontend-defaults';
import catalogFrontendPlugin from '@backstage/plugin-catalog/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';
import notificationsPlugin from '@backstage/plugin-notifications/alpha';
import catalogImportFrontendPlugin from '@backstage/plugin-catalog-import/alpha';
import catalogGraphFrontendPlugin from '@backstage/plugin-catalog-graph/alpha';
import techRadarFrontendPlugin from '@backstage-community/plugin-tech-radar/alpha';
import techdocsFrontendPlugin from '@backstage/plugin-techdocs/alpha';
import { techDocsReportIssueAddonModule } from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import { techDocsMermaidAddonModule } from 'backstage-plugin-techdocs-addon-mermaid/alpha';
import searchFrontendPlugin from '@backstage/plugin-search/alpha';
import apiDocsFrontendPlugin from '@backstage/plugin-api-docs/alpha';
import scaffolderFrontendPlugin from '@backstage/plugin-scaffolder/alpha';
import mcpChatFrontendPlugin from '@backstage-community/plugin-mcp-chat/alpha';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { FlatRoutes } from '@backstage/core-app-api';
import { RequireFeatureFlag } from './components/routing/RequireFeatureFlag';
import analyticsPlugin from '@internal/backstage-plugin-analytics';
import {
  NotMyReportPage,
  SlackNudgesPage,
} from '@internal/backstage-plugin-slack-nudges';
import { OkrCascadePage } from '@internal/backstage-plugin-okr-frontend';
import aiMarketplacePlugin from '@internal/backstage-plugin-ai-marketplace';
import aoeRadarPlugin from '@internal/backstage-plugin-aoe-radar';
import codeMigrationsPlugin from '@internal/backstage-plugin-code-migrations';
import externalDownDetectorPlugin from '@internal/backstage-plugin-external-down-detector';
import { FinopsPage } from '@internal/backstage-plugin-finops';
import { SecurityInsightsRouter } from '@internal/backstage-plugin-security-insights';
import qualityMaturityPlugin from '@internal/backstage-plugin-quality-maturity';
import {
  ScorecardLeaderboardPage,
  scorecardPlugin,
} from '@internal/backstage-plugin-scorecard';
import { appModule } from './modules/app';
import { homeModule } from './modules/home';
import { pagesModule } from './modules/pages';
import { navModule } from './modules/nav';
import { techRadarModule } from './modules/tech-radar';
import { catalogGraphModule } from './modules/catalog-graph';
import { catalogModule } from './modules/catalog';
import { techInsightsModule } from './modules/tech-insights';
import { apiDocsModule } from './modules/api-docs';
import { techdocsModule } from './modules/techdocs';
import { searchModule } from './modules/search';
import { scaffolderModule } from './modules/scaffolder';
import { mcpChatModule } from './modules/mcp-chat';
import {
  censusPlugin,
  FleetOverviewPage,
  TechSummaryPage,
  UiReposPage,
} from '@internal/backstage-plugin-census';
import arriveAgentLaunchPlatformPlugin from '@internal/backstage-plugin-arrive-agent-launch-platform';
import { cicdPromoterPlugin } from '@internal/backstage-plugin-cicd-promoter';
import { datadogPlugin } from '@internal/backstage-plugin-datadog';
import orcaPlugin from '@internal/backstage-plugin-orca-frontend';
import { NotFoundPage } from './components/error/NotFoundPage';
import { wrapRoutable } from './lib/wrapRoutable';

// `mcp-chat` gates `/mcp-chat` (`mcpChatModule`); the mcp-chat `/alpha` plugin
// declares no flag of its own.
export const appFeatureFlags = [
  {
    name: 'mcp-chat',
    pluginId: '',
    description: 'Enable the MCP Chat page',
  },
];

// Exported so route-level tests (App.test.tsx) can mount the real route table
// and assert which paths are reachable in the default feature-flag state.
export const routes = (
  <FlatRoutes>
    <Route path="/teams" element={<TeamsPage />} />
    <Route path="/slack-nudges/not-my-report" element={<NotMyReportPage />} />
    {/* PAT-565: not in the sidebar or Preview page — admin-only (isAdminGroupMember, enforced server-side by slack-nudges-backend), reached via direct URL. */}
    <Route path="/slack-nudges/*" element={<SlackNudgesPage />} />
    <Route path="/finops/*" element={<FinopsPage />} />
    <Route path="/okr-cascade" element={<OkrCascadePage />} />
    <Route path="/fleet-census" element={<FleetOverviewPage />} />
    <Route path="/tech-summary" element={<TechSummaryPage />} />
    <Route
      path="/ui-repos"
      element={wrapRoutable(<UiReposPage />, page => (
        <RequireFeatureFlag flag="fleet-census">{page}</RequireFeatureFlag>
      ))}
    />
    <Route path="/security-insights/*" element={<SecurityInsightsRouter />} />
    <Route path="/create-team" element={<TeamCreatePage />} />
    <Route
      path="/team-edit/:namespace/:kind/:name"
      element={<TeamEditPage />}
    />
    <Route
      path="/scorecard-leaderboard"
      element={wrapRoutable(<ScorecardLeaderboardPage />, page => (
        <RequireFeatureFlag flag="scorecard-v2">{page}</RequireFeatureFlag>
      ))}
    />
  </FlatRoutes>
);

// `convertLegacyAppRoot` converts these plugins' routes but not their feature
// flags, so without this the flags would vanish from Settings and the
// `RequireFeatureFlag` routes (`fleet-census`, `scorecard-v2`) become
// unreachable. Add any new flag a legacy plugin declares here. NFS plugins
// such as `orca`, `code-migrations` and `arrive-agent-launch-platform` declare
// their flags in `createFrontendPlugin` and are listed in `features` directly;
// listing one here as well would register its flag twice.
const flaggedLegacyPlugins = [
  censusPlugin,
  cicdPromoterPlugin,
  datadogPlugin,
  scorecardPlugin,
];

const pluginFeatureFlags = flaggedLegacyPlugins.flatMap(plugin =>
  Array.from(plugin.getFeatureFlags(), flag => ({
    name: flag.name,
    pluginId: plugin.getId(),
    description: `Registered in ${plugin.getId()} plugin`,
  })),
);

// Exported so the bootstrap test can assert what reaches the NFS app.
export const features: NonNullable<
  NonNullable<Parameters<typeof createApp>[0]>['features']
> = [
  convertLegacyAppOptions({
    featureFlags: [...appFeatureFlags, ...pluginFeatureFlags],
    components: {
      NotFoundErrorPage: NotFoundPage,
    },
  }),
  // NFS plugin; only declares the `orca` feature flag.
  orcaPlugin,
  // PAT-1027: `entityPage` (still legacy JSX) is converted into entity contents
  // and overview cards for the alpha catalog plugin's `page:catalog/entity`.
  // Compat ignores `FeatureFlagged` around a route and files a tab under its
  // direct child's plugin, so see the rules in `EntityPage.tsx`.
  //
  // The FlatRoutes element is passed directly: with an `AppRouter`/layout
  // root, `convertLegacyAppRoot` would also emit a clone of that layout in place
  // of `app/layout` and disable `app/nav`, hiding the nav module's sidebar.
  ...convertLegacyAppRoot(routes, { entityPage }),
  // Native NFS plugins (Phase 3); their legacy routes are gone from `routes`.
  aoeRadarPlugin,
  // Also declares the `code-migrations` flag (sidebar item only; the page is
  // not flag-gated).
  codeMigrationsPlugin,
  // PAT-1045: also declares the `agent-launch-platform` flag (gates no UI).
  arriveAgentLaunchPlatformPlugin,
  qualityMaturityPlugin,
  aiMarketplacePlugin,
  externalDownDetectorPlugin,
  analyticsPlugin,
  // PAT-1021 / PAT-1024: upstream pages from each package's `/alpha` (new
  // frontend system). They come after the converted legacy root because
  // features with the same plugin id are deduplicated and the last one wins, so
  // these replace any legacy-converted plugin of the same id. Their legacy
  // `<Route>`s are gone from `routes`: a leftover one would override the NFS
  // page.
  // PAT-1027: `/catalog` and the entity page. `catalogModule` replaces its index
  // page (user columns), list filter (persisted Owned/Starred/All) and
  // entity-presentation API, and adds the user-annotation filters.
  catalogFrontendPlugin,
  userSettingsPlugin,
  notificationsPlugin,
  // Its page wraps itself in `RequirePermission(catalogEntityCreatePermission)`.
  catalogImportFrontendPlugin,
  // Its default API is replaced by `catalogGraphModule` (worksOnBehalfOf).
  catalogGraphFrontendPlugin,
  // Page props (title, size) are `page:tech-radar` config in app-config.yaml;
  // its default API is replaced by `techRadarModule` (in-repo radar data).
  techRadarFrontendPlugin,
  // PAT-1024: `/docs` and the reader. `techdocsModule` replaces its index page
  // (persisted user-list filter) and adds the Copy Markdown addon; ReportIssue
  // and Mermaid come from their own `/alpha` modules below. Not the
  // addons-contrib default export: that also adds three more addons.
  techdocsFrontendPlugin,
  // PAT-1023: its page is replaced by `searchModule` (our custom search page);
  // its API is the same `SearchClient` the legacy plugin provided, and also
  // serves the sidebar's `SidebarSearchModal`.
  searchFrontendPlugin,
  // PAT-1022: its page is replaced by `apiDocsModule` (restores the persisted
  // Owned/Starred/All filter) and its config API by the same module's
  // backend-proxying OpenAPI widget.
  apiDocsFrontendPlugin,
  // PAT-1025: `/create` (tabbed: templates, tasks, ...); `scaffolderModule`
  // registers our custom form fields on its `api:scaffolder/form-fields`.
  scaffolderFrontendPlugin,
  // PAT-1026: the yarn-patched mcp-chat package (the patch edits a module its
  // `/alpha` page also loads). Its page is replaced by `mcpChatModule`, which
  // turns the framework header off and gates it on the app-level `mcp-chat`
  // flag; its API is the same `McpChat` client the legacy plugin provided.
  mcpChatFrontendPlugin,
  appModule,
  navModule,
  homeModule,
  pagesModule,
  techRadarModule,
  catalogGraphModule,
  catalogModule,
  techInsightsModule,
  apiDocsModule,
  techdocsModule,
  techDocsReportIssueAddonModule,
  techDocsMermaidAddonModule,
  searchModule,
  scaffolderModule,
  mcpChatModule,
];

const app = createApp({
  features,
  bindRoutes({ bind }) {
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(
        scaffolderPlugin.routes.selectedTemplate,
      ),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(
        catalogImportPlugin.routes.importPage,
      ),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
});

export default app.createRoot();
